/**
 * Ce qu'un degré **est** : son nom français, sa fonction usuelle, et la
 * phrase qui le présente.
 *
 * Pur : ni React, ni DOM, ni analyse. Une seule table, parce que le produit en
 * portait trois qui se contredisaient : une morte dans `theory-helpers`, une
 * réduite à T/SD/D dans l'atlas d'harmonie, une troisième en locutions courtes
 * dans `s0-copy`. Celle-ci remplace la première et alimente la deuxième ; la
 * troisième reste, elle répond à une autre question (voir plus bas).
 *
 * Source : `docs/theorie-musicale/05-harmonie-fonctionnelle-cadences.md`. §3.1
 * pour les noms français et la fonction usuelle du majeur, §3.2 pour les trois
 * échelles du mineur, §2 pour ce que fait chaque fonction, §4 pour les
 * substitutions, §3.4 pour la distinction sensible / sous-tonique.
 *
 * ## La phrase dit ce que l'accord est, pas ce qu'il va faire
 *
 * Les phrases que ce fichier remplace promettaient des résolutions :
 * « prépare la dominante », « appelle la résolution sur I », « mène à i ».
 * Mesuré sur le répertoire populaire du catalogue, IV y est plus fréquent que
 * V et 41 % des morceaux ne font aucune cadence. Une phrase qui promet une
 * suite que le morceau ne joue pas est fausse pour deux joueurs sur cinq, et
 * fausse exactement au moment où l'on regarde l'écran pour savoir si l'on a
 * compris.
 *
 * D'où la forme : un nom, puis ce que l'accord porte. Le seul verbe autorisé
 * décrit une sensation présente, jamais une suite attendue.
 *
 * ## Là où les systèmes se contredisent, on ne tranche pas
 *
 * La §1.5 liste les objets sur lesquels théorie des degrés,
 * *Funktionstheorie* et école américaine ne s'accordent pas réellement. Sur
 * ceux-là, la phrase se replie sur ce que les trois partagent : la triade est
 * diminuée, la note est à un ton ou à un demi-ton, l'accord partage telle note
 * avec la tonique. Et `usualFunction` vaut `null` plutôt que d'arbitrer.
 *
 * `vi` est l'exception assumée, adossée au document : « T substituée » ouvre
 * sa colonne *fonction usuelle*, et c'est la convention que le corpus
 * recommande de suivre.
 *
 * ## Le mineur a trois échelles, donc une table à part
 *
 * Et les accords joués piochent dans les trois. Transposer la table du majeur
 * y produirait des absurdités : `v` mineur n'a pas de sensible, `VII` est une
 * sous-tonique et non une sensible, `IV` majeur vient du mélodique ascendant.
 * Aucune ligne du mineur n'est déduite du majeur.
 *
 * ## Ce que ce fichier ne reprend pas
 *
 * Les locutions de `s0-copy` (« le point d'appui », « celui qui appelle la
 * suite ») ne sont pas des phrases de rôle : ce sont des groupes nominaux
 * insérés dans une autre phrase, et le parcours par cœur les compose
 * autrement. Deux formes, deux usages, et les fondre casserait la phrase de
 * l'un pour servir l'autre.
 */

import type { TonalityMode } from "./tonality";

/**
 * La couche fonctionnelle de l'école américaine, celle que le corpus
 * recommande de poser au-dessus de la théorie des degrés (§1.5).
 *
 * Trois valeurs et pas sept, parce qu'une fonction n'est pas un degré. `null`
 * quand les systèmes ne s'accordent pas sur celle de ce degré.
 */
export type DegreeFunction = "T" | "PD" | "D";

export type Degree = {
  /** Le nom français du degré (§3.1, §3.4). */
  readonly name: string;
  /** Sa fonction usuelle, ou `null` quand elle est disputée. */
  readonly usualFunction: DegreeFunction | null;
  /** Ce que ce degré est, en une phrase. */
  readonly role: string;
};

/**
 * Les sept degrés diatomiques du majeur, dans l'ordre.
 *
 * Exporté pour que rien n'ait à réécrire cette suite : un objet qui indexe les
 * degrés par leur rang la lit ici plutôt que d'en garder une copie.
 */
export const MAJOR_NUMERALS: readonly string[] = [
  "I",
  "ii",
  "iii",
  "IV",
  "V",
  "vi",
  "vii°",
];

const MAJOR: Readonly<Record<string, Degree>> = {
  I: {
    name: "tonique",
    usualFunction: "T",
    role: "C'est la tonique : le point d'appui, celui qui ne demande rien.",
  },
  ii: {
    name: "sus-tonique",
    usualFunction: "PD",
    role: "C'est la sus-tonique : la couleur de la sous-dominante, en mineur.",
  },
  iii: {
    name: "médiante",
    // Le document lui donne deux fonctions faibles, « T faible / D faible », et
    // range la tonique en premier. C'est aussi la couleur que l'atlas
    // d'harmonie lui donne depuis toujours.
    usualFunction: "T",
    role: "C'est la médiante : la tierce et la quinte de la tonique, sans sa fondamentale.",
  },
  IV: {
    name: "sous-dominante",
    usualFunction: "PD",
    role: "C'est la sous-dominante : elle ouvre, sans contredire le point d'appui.",
  },
  V: {
    name: "dominante",
    usualFunction: "D",
    role: "C'est la dominante : elle porte la sensible, la note qui frotte contre la tonique.",
  },
  vi: {
    name: "sus-dominante",
    usualFunction: "T",
    role: "C'est la tonique substituée : elle repose, mais plus sombre.",
  },
  "vii°": {
    name: "sensible",
    usualFunction: "D",
    role: "C'est l'accord de sensible : une triade diminuée, qui ne se pose pas.",
  },
  bII: {
    name: "napolitaine",
    usualFunction: "PD",
    role: "C'est la napolitaine : un degré emprunté, un demi-ton au-dessus de la tonique.",
  },
  bIII: {
    name: "médiante abaissée",
    usualFunction: null,
    role: "C'est la médiante abaissée : une couleur empruntée au mineur.",
  },
  bVI: {
    name: "sus-dominante abaissée",
    usualFunction: null,
    role: "C'est la sus-dominante abaissée : une couleur empruntée au mineur.",
  },
  bVII: {
    name: "sous-tonique",
    usualFunction: null,
    role: "C'est la sous-tonique : un ton sous la tonique, et non un demi-ton.",
  },
};

const MINOR: Readonly<Record<string, Degree>> = {
  i: {
    name: "tonique",
    usualFunction: "T",
    role: "C'est la tonique : le point d'appui, celui qui ne demande rien.",
  },
  "ii°": {
    name: "sus-tonique",
    usualFunction: "PD",
    role: "C'est la sus-tonique : diminuée en mineur, elle ne se pose pas.",
  },
  // Le mineur mélodique ascendant, seul des trois à donner une sus-tonique
  // mineure là où les deux autres donnent une triade diminuée.
  ii: {
    name: "sus-tonique",
    usualFunction: "PD",
    role: "C'est la sus-tonique : mineure, telle que la donne le mineur mélodique.",
  },
  III: {
    name: "médiante",
    usualFunction: "T",
    role: "C'est la médiante : l'accord du relatif majeur, l'éclaircie du mineur.",
  },
  iv: {
    name: "sous-dominante",
    usualFunction: "PD",
    role: "C'est la sous-dominante : elle ouvre, et elle reste sombre.",
  },
  IV: {
    name: "sous-dominante",
    usualFunction: "PD",
    role: "C'est la sous-dominante majeure : celle du mineur mélodique, qui éclaire.",
  },
  // Pas de sensible, donc pas de fonction dominante : le document est explicite
  // là-dessus, et lui en donner une ferait mentir la phrase juste au-dessus.
  v: {
    name: "dominante",
    usualFunction: null,
    role: "C'est la dominante mineure : sans sensible, elle n'appelle pas.",
  },
  V: {
    name: "dominante",
    usualFunction: "D",
    role: "C'est la dominante : majeure en mineur, elle porte la sensible.",
  },
  // « prédominante / tonique » selon l'école américaine : deux réponses, donc
  // pas de fonction ici.
  VI: {
    name: "sus-dominante",
    usualFunction: null,
    role: "C'est la sus-dominante : majeure en mineur, elle adoucit.",
  },
  "vi°": {
    name: "sus-dominante",
    usualFunction: null,
    role: "C'est la sus-dominante : diminuée, celle du mineur mélodique.",
  },
  VII: {
    name: "sous-tonique",
    usualFunction: null,
    role: "C'est la sous-tonique : un ton sous la tonique, et non un demi-ton.",
  },
  // Deux orthographes d'un même degré : la sensible du mineur est un VIIe
  // haussé, que les analyses écrivent tantôt relevé, tantôt déjà altéré.
  "vii°": {
    name: "sensible",
    usualFunction: "D",
    role: "C'est l'accord de sensible : une triade diminuée, qui ne se pose pas.",
  },
  "#vii°": {
    name: "sensible",
    usualFunction: "D",
    role: "C'est l'accord de sensible : une triade diminuée, qui ne se pose pas.",
  },
  bII: {
    name: "napolitaine",
    usualFunction: "PD",
    role: "C'est la napolitaine : un degré emprunté, un demi-ton au-dessus de la tonique.",
  },
};

/**
 * Le chiffre romain réduit à ce que les tables indexent.
 *
 * On retire le chiffrage, qui dit le renversement et la septième : `V` et `V7`
 * sont le même degré, et ces phrases ne commentent ni l'un ni l'autre. Le rond
 * du diminué et l'altération devant le chiffre restent : ils ne chiffrent pas
 * l'accord, ils en changent la triade ou la place.
 */
function canonical(roman: string): string {
  return roman
    .replace(/\/.*$/, "") // basse indiquée
    .replace(/[0-9]+/g, "") // 7, 6, 9, 11, 13
    .replace(/(maj|sus|add|m|aug|\+)/gi, "")
    .replace(/♭/g, "b")
    .replace(/♯/g, "#")
    .replace(/[ºo]/g, "°")
    .trim();
}

/**
 * Ce degré, dans ce mode, ou `null` quand la table ne le connaît pas.
 *
 * `null` est un cas ordinaire et pas une panne : les degrés secondaires et les
 * emprunts hors table en sortent, et l'appelant se contente alors de nommer le
 * degré. Dire ce que porte un `V/V` demanderait une tonalité locale, que cette
 * fonction n'a pas.
 */
export function degreeOf(roman: string, mode: TonalityMode): Degree | null {
  if (roman === "") {
    return null;
  }

  return (mode === "minor" ? MINOR : MAJOR)[canonical(roman)] ?? null;
}
